import React from 'react';
import { Badge } from '../../components/ui/badge';
import { 
  MessageSquare, Lock, Building2, Briefcase, 
  FolderKanban, Megaphone, User, Users 
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { DESIGN } from '../../components/DesignSystem';

// Thread type configurations with premium colors
const THREAD_TYPE_CONFIG = {
  private: { 
    icon: Lock, 
    label: 'Private', 
    color: '#A855F7',
    bgLight: 'rgba(168, 85, 247, 0.1)'
  },
  department: { 
    icon: Building2, 
    label: 'Department', 
    color: '#3B82F6',
    bgLight: 'rgba(59, 130, 246, 0.1)'
  },
  role: { 
    icon: Briefcase, 
    label: 'Role', 
    color: '#F59E0B',
    bgLight: 'rgba(245, 158, 11, 0.1)'
  },
  project: { 
    icon: FolderKanban, 
    label: 'Project', 
    color: '#10B981',
    bgLight: 'rgba(16, 185, 129, 0.1)'
  },
  system: { 
    icon: Megaphone, 
    label: 'System', 
    color: '#EF4444',
    bgLight: 'rgba(239, 68, 68, 0.1)'
  },
  personal: { 
    icon: User, 
    label: 'Personal', 
    color: '#64748B',
    bgLight: 'rgba(100, 116, 139, 0.1)'
  }
};

// Health indicator colors
const HEALTH_COLORS = {
  active: '#10B981',
  moderate: '#F59E0B',
  inactive: '#EF4444'
};

const ThreadList = ({ threads, selectedThreadId, onSelectThread, currentUserId }) => {
  return (
    <div className="space-y-1 p-2" data-testid="thread-list">
      {threads.map((thread) => {
        const isSelected = thread.id === selectedThreadId;
        const typeConfig = THREAD_TYPE_CONFIG[thread.type] || THREAD_TYPE_CONFIG.private;
        const TypeIcon = typeConfig.icon;
        const healthColor = HEALTH_COLORS[thread.health] || HEALTH_COLORS.moderate;
        
        return (
          <div
            key={thread.id}
            onClick={() => onSelectThread(thread)}
            className="relative cursor-pointer transition-all duration-200 rounded-xl overflow-hidden group"
            style={{ 
              backgroundColor: isSelected ? DESIGN.colors.primarySubtle : DESIGN.colors.card,
              borderLeft: isSelected ? `4px solid ${typeConfig.color}` : '4px solid transparent',
              boxShadow: isSelected ? `0 0 15px ${typeConfig.color}20` : 'none'
            }}
            data-testid={`thread-item-${thread.id}`}
          >
            <div className="p-4">
              {/* Top Row: Title + Unread Badge */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 
                  className="font-bold text-sm truncate flex-1"
                  style={{ color: isSelected ? DESIGN.colors.primary : DESIGN.colors.textPrimary }}
                >
                  {thread.title}
                </h4>
                {thread.unreadCount > 0 && (
                  <div 
                    className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ 
                      backgroundColor: DESIGN.colors.primary,
                      boxShadow: `0 0 10px ${DESIGN.colors.primary}50`
                    }}
                  >
                    {thread.unreadCount}
                  </div>
                )}
              </div>

              {/* Description preview */}
              {thread.description && (
                <p 
                  className="text-xs truncate mb-3"
                  style={{ color: DESIGN.colors.textMuted }}
                >
                  {thread.description}
                </p>
              )}

              {/* Bottom Row: Type, Members, Health */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {/* Type Badge */}
                  <div 
                    className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold"
                    style={{ 
                      backgroundColor: typeConfig.bgLight, 
                      color: typeConfig.color
                    }}
                  >
                    <TypeIcon className="w-3 h-3" />
                    {typeConfig.label}
                  </div>

                  {/* Online Status Dot with pulse */}
                  <div className="relative">
                    <div 
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: healthColor }}
                    />
                    {thread.health === 'active' && (
                      <div 
                        className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping"
                        style={{ backgroundColor: healthColor, opacity: 0.4 }}
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs" style={{ color: DESIGN.colors.textMuted }}>
                  {/* Member Count */}
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {thread.members?.length || 0}
                  </span>
                  
                  {/* Message Count */}
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" />
                    {thread.messageCount || 0}
                  </span>
                </div>
              </div>

              {/* Last Activity */}
              <div className="mt-3 text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>
                {thread.lastActivityAt 
                  ? formatDistanceToNow(new Date(thread.lastActivityAt), { addSuffix: true })
                  : 'No activity'
                }
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ThreadList;
